import {
  AssetPartDimensions,
  AxialMovementPosition,
  CustomPreviewProps,
  KILN_PART_PREVIEW_HEIGHT,
  KILN_PART_PREVIEW_Y,
  RollerParts,
  StationParts
} from '@domain/kiln-monitoring'

import {
  Bearing,
  LocationPill,
  Roller,
  StationDeflection,
  StationTyre,
  ThrustRoller
} from './AtomicParts'
import {BuildStationLocationsInput} from './KilnParts.types'
import {buildStationLocations} from './KilnParts.utils'

const renderLocationPills = ({
  id,
  pillOffsetX,
  thrustRollerPosition
}: BuildStationLocationsInput & {pillOffsetX: number}) => {
  const locationPills = buildStationLocations({id, thrustRollerPosition})

  return locationPills.map((pill) => (
    <LocationPill
      key={pill.locationNumber}
      x={pill.x + pillOffsetX}
      y={pill.y}
      text={pill.locationNumber}
    />
  ))
}

type StationPartProps = {
  id: string
  x: number
  y: number
  width: number
  height: number
  thrustRollerPosition: 'left' | 'right' | 'none'
  showLocation?: boolean
  topRollerMovement?: AxialMovementPosition
  bottomRollerMovement?: AxialMovementPosition
  customSvgPreview?: (props: CustomPreviewProps) => React.ReactNode
  widthScaleFactor?: number
  dimensions?: AssetPartDimensions
  meterToPixel?: number
}

const DEFAULT_STATION_WIDTH = 40

// eslint-disable-next-line complexity
export const StationPart = ({
  id,
  x,
  y,
  height,
  thrustRollerPosition,
  showLocation = false,
  topRollerMovement,
  bottomRollerMovement,
  customSvgPreview,
  dimensions,
  meterToPixel
}: StationPartProps) => {
  const hasThrustRoller = thrustRollerPosition !== 'none'
  const stationOffsetX = thrustRollerPosition === 'left' ? 41 : 0
  const thrustRollerOffsetX = thrustRollerPosition === 'right' ? 50 : 0
  const offsetY = showLocation ? 30 : 0
  const pillOffsetX = showLocation && thrustRollerPosition === 'left' ? 12 : 0
  const stationOffsetXPill = showLocation && thrustRollerPosition !== 'left' ? 30 : 0

  // Position 1 and Position 2 are describing the roller movement part of axial balance
  // it is represented as a horizontal shift of the whole roller in the svg based on a specific signal reading
  // if we want to shift the roller to the left, we need to instead shift all the remaining parts to the right
  const anyPosition1 = topRollerMovement === 'Position1' || bottomRollerMovement === 'Position1'
  const position1OffsetX = anyPosition1 ? 30 : 0

  const xPosition =
    dimensions?.distanceFrom0 && meterToPixel ? dimensions.distanceFrom0 * meterToPixel : x
  const stationWidth =
    dimensions?.width && meterToPixel ? dimensions.width * meterToPixel : DEFAULT_STATION_WIDTH

  return (
    <svg
      id={id}
      x={xPosition - position1OffsetX + stationWidth / 2}
      y={y}
      width={stationWidth + 80}
      height={height}
    >
      {customSvgPreview ? (
        customSvgPreview({
          x: 0,
          y: KILN_PART_PREVIEW_Y,
          width: stationWidth,
          height: KILN_PART_PREVIEW_HEIGHT
        })
      ) : (
        <>
          <svg x={stationOffsetX + stationOffsetXPill} y={offsetY}>
            {renderStationRollers({
              id,
              topRollerMovement,
              bottomRollerMovement,
              position1OffsetX
            })}
            <StationTyre id={`${id}_${StationParts.TYRE}`} x={14 + position1OffsetX} y={38} />
          </svg>
          {hasThrustRoller && (
            <ThrustRoller
              id={`${id}_${StationParts.THRUST_ROLLER}`}
              x={thrustRollerOffsetX + stationOffsetXPill + position1OffsetX}
              y={92 + offsetY}
            />
          )}
        </>
      )}
      {showLocation && renderLocationPills({id, pillOffsetX, thrustRollerPosition})}
    </svg>
  )
}

const renderStationRollers = ({
  id,
  topRollerMovement,
  bottomRollerMovement,
  position1OffsetX
}: {
  id: string
  topRollerMovement?: AxialMovementPosition
  bottomRollerMovement?: AxialMovementPosition
  position1OffsetX: number
}) => (
  <>
    <StationRoller
      idPrefix={`${id}_${StationParts.SUPPORT_ROLLER_TOP}`}
      y={0}
      position="top"
      x={position1OffsetX + getRollerShift(topRollerMovement)}
    />
    {topRollerMovement === 'Position1' && <ArrowLeft x={5} y={26} />}
    {topRollerMovement === 'Position2' && <ArrowRight x={position1OffsetX + 75} y={26} />}
    <StationRoller
      idPrefix={`${id}_${StationParts.SUPPORT_ROLLER_BOTTOM}`}
      y={194}
      position="bottom"
      x={position1OffsetX + getRollerShift(bottomRollerMovement)}
    />
    {bottomRollerMovement === 'Position1' && <ArrowLeft x={5} y={202} />}
    {bottomRollerMovement === 'Position2' && <ArrowRight x={position1OffsetX + 75} y={202} />}
  </>
)

const getRollerShift = (position?: AxialMovementPosition) => {
  const rollerShift = 5
  if (position === 'Position1') return -rollerShift
  if (position === 'Position2') return rollerShift
  return 0
}

export const StationRoller = ({
  idPrefix,
  y,
  position,
  x = 0
}: {
  idPrefix: string
  y: number
  position: 'top' | 'bottom'
  x?: number
}) => {
  const yOffset = position === 'top' ? 18 : 0
  const deflectorY = position === 'top' ? 0 : 34

  return (
    <svg width="68" height="50" x={x} y={y}>
      <StationDeflection
        id={`${idPrefix}_${RollerParts.SUPPORT_ROLLER_DEFLECTOR}`}
        x="28"
        y={deflectorY}
      />
      <Bearing
        id={`${idPrefix}_${RollerParts.SUPPORT_ROLLER_BEARING_LEFT}`}
        x="0"
        y={8 + yOffset}
      />
      <Bearing
        id={`${idPrefix}_${RollerParts.SUPPORT_ROLLER_BEARING_RIGHT}`}
        x="34"
        y={8 + yOffset}
      />
      <Roller id={`${idPrefix}_${RollerParts.SUPPORT_ROLLER_ROLLER}`} x={12} y={yOffset} />
    </svg>
  )
}

const ArrowRight = (props) => (
  <svg
    width={17}
    height={16}
    viewBox="0 0 17 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M16.7071 8.70711C17.0976 8.31658 17.0976 7.68342 16.7071 7.29289L10.3431 0.928932C9.95262 0.538407 9.31946 0.538407 8.92893 0.928932C8.53841 1.31946 8.53841 1.95262 8.92893 2.34315L14.5858 8L8.92893 13.6569C8.53841 14.0474 8.53841 14.6805 8.92893 15.0711C9.31946 15.4616 9.95262 15.4616 10.3431 15.0711L16.7071 8.70711ZM16 7L8.74228e-08 7L-8.74228e-08 9L16 9L16 7Z"
      fill="#54708C"
    />
  </svg>
)

const ArrowLeft = (props) => (
  <svg
    width={17}
    height={16}
    viewBox="0 0 17 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M0.292893 7.29289C-0.097631 7.68342 -0.0976312 8.31658 0.292893 8.70711L6.65685 15.0711C7.04738 15.4616 7.68054 15.4616 8.07107 15.0711C8.46159 14.6805 8.46159 14.0474 8.07107 13.6569L2.41421 8L8.07107 2.34315C8.46159 1.95262 8.46159 1.31946 8.07107 0.928933C7.68054 0.538409 7.04738 0.538409 6.65686 0.928933L0.292893 7.29289ZM1 9L17 9L17 7L1 7L1 9Z"
      fill="#54708C"
    />
  </svg>
)
